import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { createFormSchema } from '@/features/form-builder/model/validation';
import { axiosInstance } from '@/shared/api/client';
import type {
  AgentExecutionState,
  AgentExecutionStep,
  FormValues,
  SerializableFormValues,
  StepProgress,
} from '@/shared/types';
import type { FormButtonConfig, FormField, FormSection } from '@/shared/types/form';
import { Button } from '@/shared/ui/button';
import {
  SegmentedButton,
  SegmentedButtonItem,
  SegmentedButtonList,
} from '@/shared/ui/segmented-button';
import type {
  BlockExecutionError,
  BlockExecutionProgress,
  BlockExecutionStatus,
} from '../model/useBlockExecution';
import { ExecutionProgress } from './ExecutionProgress';
import { FormFieldRenderer } from './form-fields';
import { SampleDataSection } from './SampleDataSection';

/**
 * 탭 옵션
 */
export const TAB_OPTIONS: { label: string; value: string; isDefault?: boolean }[] = [
  { label: '정보 입력', value: 'input', isDefault: true },
  { label: '진행상황', value: 'progress' },
];

/**
 * 기본 탭
 */
const DEFAULT_TAB = TAB_OPTIONS.find((tab) => tab.isDefault)?.value;

/**
 * 탭 값 타입
 */
export type TabValue = (typeof TAB_OPTIONS)[number]['value'];

/** 좌측 패널 탭 값 타입 */
export type LeftPanelTabValue = 'input' | 'progress';

/**
 * InputPanel 공통 Props (Desktop/Mobile에서 공유)
 */
export interface InputPanelProps {
  executionState: AgentExecutionState;
  onSubmit: (values: FormValues) => void;
  onReset: () => void;
  formSchema?: FormSection[];
  buttons?: FormButtonConfig[];
  steps?: AgentExecutionStep[];
  agentId?: string;
  /** 읽기 전용 모드 (operator 모드에서 사용) */
  readOnly?: boolean;
  /** 초기 폼 값 (operator 모드에서 이전 입력값 표시, File 제외) */
  initialFormValues?: SerializableFormValues;
  /** 비활성화할 탭 목록 */
  disabledTabs?: readonly LeftPanelTabValue[];
  /** SSE 블록 실행 상태 */
  blockStatus?: BlockExecutionStatus;
  /** SSE 블록 진행 상태 */
  blockProgress?: BlockExecutionProgress;
  /** SSE 블록 에러 정보 */
  blockError?: BlockExecutionError | null;
  /** 에이전트 정보 로딩 중 여부 (버튼 비활성화용) */
  isAgentLoading?: boolean;
  /** 에이전트 slug (샘플 데이터 표시용) */
  agentSlug?: string;
}

/**
 * InputPanel 상태 및 로직 훅
 * Desktop InputPanel과 Mobile Drawer에서 공유
 */
/**
 * formSchema에서 defaultValue를 추출하여 초기 폼 값 생성
 */
function getDefaultFormValues(formSchema?: FormSection[]): SerializableFormValues {
  if (!formSchema) return {};

  const defaults: SerializableFormValues = {};
  for (const section of formSchema) {
    for (const field of section.fields) {
      if (field.defaultValue !== undefined) {
        defaults[field.id] = field.defaultValue;
      }
    }
  }
  return defaults;
}

export function useInputPanelState({
  executionState,
  onSubmit,
  onReset,
  formSchema,
  agentId,
  agentSlug,
  readOnly = false,
  initialFormValues,
  disabledTabs = [],
  isAgentLoading = false,
}: InputPanelProps) {
  // operator 모드에서 진행상황 탭이 기본
  const getDefaultTab = (): TabValue => {
    if (readOnly && !disabledTabs.includes('progress')) {
      return 'progress';
    }
    return DEFAULT_TAB as TabValue;
  };

  const [activeTab, setActiveTab] = useState<TabValue>(getDefaultTab());
  // 초기 폼 값 설정: formSchema의 defaultValue → initialFormValues 순으로 병합
  const [formValues, setFormValues] = useState<SerializableFormValues>(() => ({
    ...getDefaultFormValues(formSchema),
    ...initialFormValues,
  }));
  const [buttonLoadingStates, setButtonLoadingStates] = useState<Record<string, boolean>>({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const { isRunning, result, error } = executionState;

  // formSchema가 비동기로 로드될 때 defaultValue 적용
  useEffect(() => {
    if (!formSchema) return;

    const defaults = getDefaultFormValues(formSchema);
    if (Object.keys(defaults).length > 0) {
      setFormValues((prev) => {
        // 이미 값이 있는 필드는 덮어쓰지 않음
        const merged = { ...defaults };
        for (const key of Object.keys(prev)) {
          if (prev[key] !== undefined && prev[key] !== '') {
            merged[key] = prev[key];
          }
        }
        return merged;
      });
    }
  }, [formSchema]);

  // detail 모드에서 progress 탭이 비활성화되어 있는데 progress 탭이 선택된 경우 input으로 전환
  useEffect(() => {
    if (!readOnly && disabledTabs.includes('progress') && activeTab === 'progress') {
      setActiveTab('input');
    }
  }, [readOnly, disabledTabs, activeTab]);

  // 탭 비활성화 여부 확인
  const isTabDisabled = (tabValue: LeftPanelTabValue): boolean => {
    return disabledTabs.includes(tabValue);
  };

  // 탭 변경 핸들러 (비활성화된 탭은 선택 불가)
  const handleTabChange = (tabValue: TabValue) => {
    if (!isTabDisabled(tabValue as LeftPanelTabValue)) {
      setActiveTab(tabValue);
    }
  };

  const isFieldDisabled = (field: FormField): boolean => {
    // readOnly 모드에서는 모든 필드 비활성화
    if (readOnly) return true;
    if (isRunning) return true;
    if (!field.disabledWhen) return false;

    const { field: conditionField, value: conditionValue } = field.disabledWhen;
    const conditionFieldValue = formValues[conditionField];

    return (
      conditionFieldValue === conditionValue ||
      (conditionValue === false && conditionFieldValue === undefined)
    );
  };

  const isFieldVisible = (field: FormField): boolean => {
    if (!field.visibleWhen) return true;

    const { field: conditionField, value: conditionValue } = field.visibleWhen;
    const conditionFieldValue = formValues[conditionField];

    return conditionFieldValue === conditionValue;
  };

  const handleFieldChange = (fieldId: string, value: string | number | boolean) => {
    setFormValues((prev) => ({ ...prev, [fieldId]: value }));
    if (fieldErrors[fieldId]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[fieldId];
        return next;
      });
    }
  };

  const isButtonVisible = (button: FormButtonConfig): boolean => {
    if (!button.visibleWhen) return true;

    const { field: conditionField, value: conditionValue } = button.visibleWhen;

    if (conditionField === 'hasResult') {
      return conditionValue === true ? !!(result || error) : !(result || error);
    }

    const conditionFieldValue = formValues[conditionField];
    return conditionFieldValue === conditionValue;
  };

  const handleButtonClick = async (button: FormButtonConfig) => {
    if (button.type === 'submit') {
      return;
    }

    if (button.type === 'button' && button.endpoint) {
      setButtonLoadingStates((prev) => ({ ...prev, [button.id]: true }));

      try {
        const method = button.method || 'POST';
        const endpoint = button.endpoint.replace('{agentId}', agentId || '');

        await axiosInstance.request({
          method,
          url: endpoint,
          data: formValues,
        });

        toast.success('요청이 성공적으로 처리되었습니다.');
      } catch (err) {
        console.error('Button API call failed:', err);
      } finally {
        setButtonLoadingStates((prev) => ({ ...prev, [button.id]: false }));
      }
    }
  };

  const validateForm = (): boolean => {
    if (!formSchema || formSchema.length === 0) {
      return true;
    }

    try {
      const schema = createFormSchema(formSchema);
      schema.parse(formValues);
      setFieldErrors({});
      return true;
    } catch (err) {
      if (err instanceof Error && 'issues' in err) {
        const zodError = err as { issues: Array<{ path: string[]; message: string }> };
        const errors: Record<string, string> = {};

        zodError.issues.forEach((issue) => {
          const fieldName = issue.path[0];
          if (fieldName) {
            errors[fieldName] = issue.message;
          }
        });

        setFieldErrors(errors);

        const firstErrorField = Object.keys(errors)[0];
        if (firstErrorField) {
          const errorElement = document.getElementById(firstErrorField);
          if (errorElement) {
            errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            errorElement.focus();
          }
        }

        const firstError = Object.values(errors)[0];
        if (firstError) {
          toast.error(firstError);
        }
      }

      return false;
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    onSubmit(formValues);
  };

  const handleReset = () => {
    setFormValues({});
    setFieldErrors({});
    onReset();
  };

  // ===== 뉴스 클리핑 전용: 이메일 + 개인정보 동의 체크에 따른 실행 버튼 disabled =====
  const isNewsClippingAgent =
    agentSlug === 'news_clipping_keyword' || agentSlug === 'news-clipping-keyword';

  // 이메일을 입력한 경우에만 개인정보 동의 여부를 강제
  const emailValue = formValues.email;
  const hasEmail =
    typeof emailValue === 'string'
      ? emailValue.trim() !== ''
      : emailValue === true || emailValue === false
        ? emailValue
        : !!emailValue;

  const privacyConsentValue = formValues.privacyConsent;
  const isPrivacyConsented = privacyConsentValue === true;

  // Truth table (뉴스 클리핑):
  // - email X, consent X → 실행 가능 (이메일 전송 안 함)
  // - email X, consent O → 비활성화 (의미 없는 동의)
  // - email O, consent X → 비활성화 (동의 없이 이메일 전송 불가)
  // - email O, consent O → 실행 가능 (이메일 전송)
  const isSubmitDisabledByPrivacy = isNewsClippingAgent && hasEmail !== isPrivacyConsented;

  return {
    activeTab,
    setActiveTab: handleTabChange,
    formValues,
    buttonLoadingStates,
    fieldErrors,
    isRunning,
    result,
    error,
    readOnly,
    isAgentLoading,
    isFieldDisabled,
    isFieldVisible,
    isTabDisabled,
    handleFieldChange,
    isButtonVisible,
    handleButtonClick,
    handleSubmit,
    handleReset,
    isSubmitDisabled: isSubmitDisabledByPrivacy,
  };
}

/**
 * 폼 콘텐츠 렌더링 (입력 필드 영역)
 */
interface FormContentProps {
  formSchema?: FormSection[];
  formValues: Record<string, string | number | boolean>;
  fieldErrors: Record<string, string>;
  isFieldVisible: (field: FormField) => boolean;
  isFieldDisabled: (field: FormField) => boolean;
  handleFieldChange: (fieldId: string, value: string | number | boolean) => void;
}

export function FormContent({
  formSchema,
  formValues,
  fieldErrors,
  isFieldVisible,
  isFieldDisabled,
  handleFieldChange,
}: FormContentProps) {
  if (!formSchema) {
    return (
      <div className="flex items-center justify-center h-40 text-muted-foreground">
        폼 설정을 불러오는 중입니다...
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {formSchema.map((section) => (
        <div key={section.id} className="flex flex-col gap-6">
          {section.fields.filter(isFieldVisible).map((field) => (
            <div key={field.id} className={field.visibleWhen ? '-mt-4' : ''}>
              <FormFieldRenderer
                field={field}
                value={formValues[field.id] ?? field.defaultValue ?? ''}
                onChange={(value) => handleFieldChange(field.id, value)}
                disabled={isFieldDisabled(field)}
                error={fieldErrors[field.name]}
              />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

/**
 * 진행상황 콘텐츠 렌더링
 */
interface ProgressContentProps {
  /** SSE 블록 실행 상태 */
  blockStatus?: BlockExecutionStatus;
  /** SSE 블록 진행 상태 */
  blockProgress?: BlockExecutionProgress;
  /** SSE 블록 에러 정보 */
  blockError?: BlockExecutionError | null;
  /** 실행 단계 정의 */
  steps?: AgentExecutionStep[];
}

/**
 * SSE 블록 진행 상태를 ExecutionProgress가 기대하는 AgentExecutionState로 변환
 */
function createExecutionStateFromSSE(
  blockStatus: BlockExecutionStatus = 'idle',
  blockProgress: BlockExecutionProgress = {
    currentStepIndex: 0,
    totalSteps: 0,
    currentStepId: null,
    stepProgress: null,
    stepMessage: null,
  },
  blockError: BlockExecutionError | null = null,
  steps: AgentExecutionStep[] = [],
): AgentExecutionState {
  const stepsMap = new Map<string, StepProgress>();

  // currentStepIndex를 기반으로 각 스텝의 상태 계산
  steps.forEach((step, index) => {
    let status: StepProgress['status'];

    if (blockStatus === 'completed') {
      // 완료 상태면 모든 스텝이 completed
      status = 'completed';
    } else if (blockStatus === 'failed' && blockError?.stepId === step.id) {
      // 해당 스텝에서 실패
      status = 'failed';
    } else if (index < blockProgress.currentStepIndex) {
      // 현재 인덱스 이전 스텝은 완료
      status = 'completed';
    } else if (index === blockProgress.currentStepIndex && blockStatus === 'running') {
      // 현재 실행 중인 스텝
      status = 'running';
    } else {
      // 아직 실행되지 않은 스텝
      status = 'pending';
    }

    stepsMap.set(step.id, {
      stepId: step.id,
      status,
      percentage:
        status === 'completed' ? 100 : status === 'running' ? (blockProgress.stepProgress ?? 0) : 0,
    });
  });

  return {
    executionId: null,
    currentStepId: blockProgress.currentStepId,
    steps: stepsMap,
    logs: [],
    intermediateData: [],
    isRunning: blockStatus === 'running' || blockStatus === 'connecting',
    error: blockError
      ? {
          type: 'error',
          code: blockError.code,
          message: blockError.message,
          stepId: blockError.stepId,
          timestamp: new Date().toISOString(),
        }
      : null,
    result: blockStatus === 'completed' ? {} : null,
  };
}

export function ProgressContent({
  blockStatus,
  blockProgress,
  blockError,
  steps,
}: ProgressContentProps) {
  // SSE 데이터를 AgentExecutionState 형식으로 변환
  const executionState = useMemo(
    () => createExecutionStateFromSSE(blockStatus, blockProgress, blockError, steps),
    [blockStatus, blockProgress, blockError, steps],
  );

  return <ExecutionProgress executionState={executionState} steps={steps} />;
}

/**
 * 버튼 영역 렌더링
 */
interface ButtonSectionProps {
  buttons?: FormButtonConfig[];
  buttonLoadingStates: Record<string, boolean>;
  isRunning: boolean;
  result: unknown;
  error: unknown;
  isButtonVisible: (button: FormButtonConfig) => boolean;
  handleButtonClick: (button: FormButtonConfig) => void;
  handleReset: () => void;
  /** 에이전트 정보 로딩 중 여부 */
  isAgentLoading?: boolean;
  /** 추가적인 submit 비활성화 조건 (에이전트별 커스텀 로직) */
  isSubmitDisabled?: boolean;
}

export function ButtonSection({
  buttons,
  buttonLoadingStates,
  isRunning,
  result,
  error,
  isButtonVisible,
  handleButtonClick,
  handleReset,
  isAgentLoading = false,
  isSubmitDisabled = false,
}: ButtonSectionProps) {
  if (buttons && buttons.length > 0) {
    return (
      <div className="flex gap-3">
        {buttons.filter(isButtonVisible).map((button) => {
          const isLoading = buttonLoadingStates[button.id] || false;
          const isDisabled =
            isRunning ||
            isLoading ||
            button.disabled ||
            (button.type === 'submit' && (isRunning || isAgentLoading || isSubmitDisabled));

          return (
            <Button
              key={button.id}
              type={button.type}
              variant={button.variant || 'default'}
              disabled={isDisabled}
              onClick={() => handleButtonClick(button)}
              className={
                button.variant === 'default'
                  ? 'h-12 w-full rounded bg-[#0084ff] text-sm font-bold hover:bg-[#0084ff]/90'
                  : 'h-12 rounded'
              }
            >
              {isLoading
                ? '처리 중...'
                : button.type === 'submit' && isRunning
                  ? '워크플로우 실행 중...'
                  : button.label}
            </Button>
          );
        })}
      </div>
    );
  }

  return (
    <div className="flex gap-3">
      <Button
        type="submit"
        disabled={isRunning || isAgentLoading}
        className="h-12 w-full rounded bg-[#0084ff] text-sm font-bold hover:bg-[#0084ff]/90"
      >
        {isRunning
          ? '워크플로우 실행 중...'
          : isAgentLoading
            ? '불러오는 중...'
            : '워크플로우 실행'}
      </Button>

      {!!(result || error) && (
        <Button type="button" variant="outline" onClick={handleReset} className="h-12 rounded">
          초기화
        </Button>
      )}
    </div>
  );
}

/**
 * useInputPanelState 훅의 반환 타입
 */
export type InputPanelState = ReturnType<typeof useInputPanelState>;

/**
 * InputPanel에 전달 가능한 Props
 */
interface InputPanelComponentProps extends InputPanelProps {
  /** 외부에서 공유할 상태 (없으면 내부에서 생성) */
  sharedState?: InputPanelState;
}

/**
 * 좌측 패널: 입력 폼 섹션 + 실행 진행 상황 (Desktop)
 *
 * 레이아웃 구조:
 * - 상단 고정: Segmented Button (탭 전환)
 * - 중간 스크롤: 폼 필드 영역
 * - 하단 고정: 워크플로우 실행 버튼
 */
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import 'overlayscrollbars/overlayscrollbars.css';

export function InputPanel(props: InputPanelComponentProps) {
  const {
    formSchema,
    buttons,
    steps,
    sharedState,
    blockStatus,
    blockProgress,
    blockError,
    agentSlug,
  } = props;
  const internalState = useInputPanelState(props);
  const state = sharedState ?? internalState;

  return (
    <div className="flex h-full flex-col">
      {/* 상단 고정: Segmented Button */}
      <div className="shrink-0 pb-5">
        <SegmentedButton
          value={state.activeTab}
          onValueChange={(v) => state.setActiveTab(v as TabValue)}
        >
          <SegmentedButtonList size="sm" className="w-full">
            {TAB_OPTIONS.map((tab) => {
              const isDisabled = state.isTabDisabled(tab.value as LeftPanelTabValue);
              return (
                <SegmentedButtonItem
                  key={tab.value}
                  value={tab.value}
                  size="sm"
                  className={`flex-1 ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                  disabled={isDisabled}
                >
                  {tab.label}
                </SegmentedButtonItem>
              );
            })}
          </SegmentedButtonList>
        </SegmentedButton>
      </div>

      {/* 중간 스크롤 영역: 폼 필드 또는 진행상황 */}
      <OverlayScrollbarsComponent
        className="flex-1 min-h-0"
        options={{
          scrollbars: {
            visibility: 'auto',
            autoHide: 'scroll',
            autoHideDelay: 800,
            autoHideSuspend: false,
          },
        }}
        defer
      >
        <form onSubmit={state.handleSubmit} className="min-h-full">
          {state.activeTab === 'input' ? (
            <>
              <FormContent
                formSchema={formSchema}
                formValues={state.formValues}
                fieldErrors={state.fieldErrors}
                isFieldVisible={state.isFieldVisible}
                isFieldDisabled={state.isFieldDisabled}
                handleFieldChange={state.handleFieldChange}
              />
              {/* 샘플 데이터 섹션 (지원 에이전트에만 표시) */}
              {!state.readOnly && (
                <div className="pt-6">
                  <SampleDataSection
                    agentSlug={agentSlug}
                    onFieldChange={state.handleFieldChange}
                  />
                </div>
              )}
              {/* 버튼 영역: 폼과 함께 스크롤, 스크롤이 길면 sticky */}
              {!state.readOnly && (
                <div className="sticky bottom-0 bg-background pt-4">
                  <ButtonSection
                    buttons={buttons}
                    buttonLoadingStates={state.buttonLoadingStates}
                    isRunning={state.isRunning}
                    result={state.result}
                    error={state.error}
                    isButtonVisible={state.isButtonVisible}
                    handleButtonClick={state.handleButtonClick}
                    handleReset={state.handleReset}
                    isAgentLoading={state.isAgentLoading}
                    isSubmitDisabled={state.isSubmitDisabled}
                  />
                </div>
              )}
            </>
          ) : (
            <ProgressContent
              blockStatus={blockStatus}
              blockProgress={blockProgress}
              blockError={blockError}
              steps={steps}
            />
          )}
        </form>
      </OverlayScrollbarsComponent>
    </div>
  );
}
